import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { api } from "../../../services/api/axios";

// Which payout partners can convert automatically, and which have it on.
const fetchAutoConversionPartners = async () => {
  const { data } = await api.get("/v1/admin/treasury/auto-conversion");
  return data.data;
};

export const useAutoConversionPartners = () => {
  return useQuery({
    queryKey: ["auto-conversion-partners"],
    queryFn: fetchAutoConversionPartners,
    refetchOnWindowFocus: false,
  });
};

const setAutoConversion = async ({ partner, enabled }) => {
  const { data } = await api.patch(`/v1/admin/treasury/auto-conversion/${encodeURIComponent(partner)}`, { enabled });
  return data.data;
};

export const useSetAutoConversion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setAutoConversion,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["auto-conversion-partners"] });
      toast.success(`Automatic conversion ${result.enabled ? "switched on" : "switched off"} for ${result.partner}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Could not update automatic conversion");
    },
  });
};

// Every time a payout asked for an automatic top-up, including the ones that
// were off or skipped. Newest first.
const fetchAutoConversionAttempts = async ({ partner, action, limit }) => {
  const { data } = await api.get("/v1/admin/treasury/auto-conversion/attempts", {
    params: { ...(partner && { partner }), ...(action && { action }), limit },
  });
  return data.data;
};

export const useAutoConversionAttempts = ({ partner, action, limit = 50, enabled = true }) => {
  return useQuery({
    queryKey: ["auto-conversion-attempts", partner, action, limit],
    queryFn: () => fetchAutoConversionAttempts({ partner, action, limit }),
    enabled,
    refetchOnWindowFocus: false,
  });
};

// The caps on one automatic conversion and on a day's total, in USD.
export const useAutoConversionLimits = () => {
  return useQuery({
    queryKey: ["auto-conversion-limits"],
    queryFn: async () => {
      const { data } = await api.get("/v1/admin/treasury/auto-conversion/limits");
      return data.data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useSetAutoConversionLimits = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (limits) => {
      const { data } = await api.put("/v1/admin/treasury/auto-conversion/limits", limits);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auto-conversion-limits"] });
      toast.success("Automatic conversion limits saved");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Could not save the limits");
    },
  });
};
